<script setup>
import {ref, reactive, computed, onMounted, onBeforeUnmount} from 'vue'

const props = defineProps({
    // The currently applied search parameters. Only the filter keys are edited here.
    searchParameters: {
        type: Object,
        required: true,
    },
    salespersons: {
        type: Array,
        default: () => [],
    },
})

const emit = defineEmits(['apply'])

const defaultFilters = () => ({
    salesperson: 'all',
    tags: [],
    excludeTags: [],
    emailStatus: 'regardless-email',
    phoneStatus: 'regardless-phone',
    unsubscribedStatus: 'regardless-unsubscribed',
})

const pickFilters = (source) => ({
    salesperson: source.salesperson,
    tags: [...source.tags],
    excludeTags: [...source.excludeTags],
    emailStatus: source.emailStatus,
    phoneStatus: source.phoneStatus,
    unsubscribedStatus: source.unsubscribedStatus,
})

const root = ref(null)
const open = ref(false)
const draft = reactive(defaultFilters())

const activeFilterCount = computed(() => {
    const defaults = defaultFilters()
    const params = props.searchParameters

    return [
        String(params.salesperson) !== defaults.salesperson,
        params.tags.length > 0,
        params.excludeTags.length > 0,
        params.emailStatus !== defaults.emailStatus,
        params.phoneStatus !== defaults.phoneStatus,
        params.unsubscribedStatus !== defaults.unsubscribedStatus,
    ].filter(Boolean).length
})

const emailOptions = [
    { label: _mft('crm:filters.hasEmailAddress.withEmail'), name: 'with-email' },
    { label: _mft('crm:filters.hasEmailAddress.withoutEmail'), name: 'without-email' },
    { label: _mft('crm:filters.hasEmailAddress.both'), name: 'regardless-email' },
]

const phoneOptions = [
    { label: _mft('crm:filters.hasPhoneNumber.withPhone'), name: 'with-phone' },
    { label: _mft('crm:filters.hasPhoneNumber.withoutPhone'), name: 'without-phone' },
    { label: _mft('crm:filters.hasPhoneNumber.both'), name: 'regardless-phone' },
]

const newsletterOptions = [
    { label: _mft('crm:filters.unsubscribedStatus.subscribed'), name: 'subscribed' },
    { label: _mft('crm:filters.unsubscribedStatus.unsubscribed'), name: 'unsubscribed' },
    { label: _mft('crm:filters.unsubscribedStatus.both'), name: 'regardless-unsubscribed' },
]

const openPanel = () => {
    Object.assign(draft, pickFilters(props.searchParameters))
    open.value = true
}

const closePanel = () => {
    open.value = false
}

const togglePanel = () => {
    open.value ? closePanel() : openPanel()
}

const clearAll = () => {
    Object.assign(draft, defaultFilters())
}

const apply = () => {
    emit('apply', pickFilters(draft))
    closePanel()
}

const onKeydown = (e) => {
    if (e.key === 'Escape' && open.value) {
        closePanel()
    }
}

// pointerdown rather than click: a click can detach its target (e.g. removing a
// tag chip) before this runs, which would make it look like an outside click
const onPointerdown = (e) => {
    if (open.value && root.value && ! root.value.contains(e.target)) {
        closePanel()
    }
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    document.addEventListener('pointerdown', onPointerdown)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    document.removeEventListener('pointerdown', onPointerdown)
})
</script>

<template>
    <div ref="root">
        <button-base
            size="lg"
            :style-type="open ? 'tinted' : 'secondary'"
            :aria-expanded="open"
            @click="togglePanel"
        >
            <template #icon>
                <i class="fa-light fa-sliders mr-1"></i>
            </template>
            <span>
                <span>{{ _mft('crm:filters.button') }}</span>
                <span
                    v-if="activeFilterCount > 0"
                    class="absolute flex items-center justify-center w-5 h-5 text-xs font-semibold text-white rounded-full -top-3 -right-5 bg-myflowPurple"
                >
                    {{ activeFilterCount }}
                </span>
            </span>
        </button-base>

        <!-- Positioned against the nearest `relative` ancestor (the search row),
             so the panel lines up with the row's right edge on every screen size -->
        <div
            v-show="open"
            class="absolute right-0 z-50 w-full p-6 mt-2 bg-white border border-gray-200 shadow-xl top-full sm:w-[36rem] rounded-2xl"
        >
            <div class="grid items-center grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-4">
                <label class="font-medium text-gray-700">
                    {{ _mft('crm:filters.owner.title') }}
                </label>
                <input-select
                    :content="draft.salesperson"
                    :options="props.salespersons"
                    @update:content="draft.salesperson = $event"
                    class="!mb-0"
                />

                <label class="self-start pt-2.5 font-medium text-gray-700">
                    {{ _mft('crm:filters.tags.title') }}
                </label>
                <tags-multi-select-v2
                    :allow-creation="false"
                    :placeholder="_mft('crm:searchTags')"
                    :pre-selected-tags="draft.tags"
                    @tagsSelected="draft.tags = $event.map(tag => tag.id)"
                />

                <label class="self-start pt-2.5 font-medium text-gray-700">
                    {{ _mft('crm:excludeTags') }}
                </label>
                <tags-multi-select-v2
                    :allow-creation="false"
                    :placeholder="_mft('crm:excludeTags')"
                    :pre-selected-tags="draft.excludeTags"
                    @tagsSelected="draft.excludeTags = $event.map(tag => tag.id)"
                />
            </div>

            <hr class="my-5 border-gray-100">

            <div class="grid items-center grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-4">
                <label class="font-medium text-gray-700">
                    {{ _mft('crm:filters.hasEmailAddress.title') }}
                </label>
                <button-bar
                    :options="emailOptions"
                    :selected-option-name="draft.emailStatus"
                    :responsive="false"
                    size="md"
                    class="!mb-0"
                    @select="draft.emailStatus = $event.name"
                />

                <label class="font-medium text-gray-700">
                    {{ _mft('crm:filters.hasPhoneNumber.title') }}
                </label>
                <button-bar
                    :options="phoneOptions"
                    :selected-option-name="draft.phoneStatus"
                    :responsive="false"
                    size="md"
                    class="!mb-0"
                    @select="draft.phoneStatus = $event.name"
                />

                <label class="font-medium text-gray-700">
                    {{ _mft('crm:filters.unsubscribedStatus.title') }}
                </label>
                <button-bar
                    :options="newsletterOptions"
                    :selected-option-name="draft.unsubscribedStatus"
                    :responsive="false"
                    size="md"
                    class="!mb-0"
                    @select="draft.unsubscribedStatus = $event.name"
                />
            </div>

            <hr class="my-5 border-gray-100">

            <div class="flex items-center justify-between gap-2">
                <button
                    type="button"
                    class="text-sm text-gray-700 mf-link"
                    @click="clearAll"
                >
                    {{ _mft('crm:filters.resetToDefault') }}
                </button>

                <div class="flex items-center gap-2">
                    <button-base
                        style-type="secondary"
                        @click="closePanel"
                    >
                        {{ _mft('shared:action.cancel') }}
                    </button-base>
                    <button-base
                        style-type="primary"
                        @click="apply"
                    >
                        {{ _mft('crm:filters.apply') }}
                    </button-base>
                </div>
            </div>
        </div>
    </div>
</template>

<style>

</style>
